// pages/eventos/admin/CriarEventoPage.jsx

import React, { useMemo, useRef, useState } from "react";
import axios from "../../../axiosConfig";
import { QRCodeCanvas } from "qrcode.react";
import * as htmlToImage from "html-to-image";
import download from "downloadjs";
import {
  CalendarDays,
  CheckCircle2,
  Download,
  Gift,
  Link as LinkIcon,
  Plus,
  Save,
  ToggleLeft,
  Trash2,
} from "lucide-react";

import "./CriarEventoPage.css";

const CriarEventoPage = () => {
  const [form, setForm] = useState({
    slug: "",
    nome_evento: "",
    descricao: "",
    data_inicio: "",
    data_fim: "",
    limite_participantes: 10,
    ativo: true,
  });

  const [premios, setPremios] = useState([]);
  const [mensagem, setMensagem] = useState("");
  const [erro, setErro] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [eventoCriado, setEventoCriado] = useState(null);

  const qrCodeRef = useRef(null);

  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";

  const linkEvento = eventoCriado ? `${baseUrl}/eventos/${eventoCriado}` : "";

  const totalPremios = useMemo(
    () => premios.reduce((sum, p) => sum + Number(p.quantidade || 0), 0),
    [premios]
  );

  const premiosInsuficientes =
    Number(form.limite_participantes || 0) > 0 &&
    totalPremios < Number(form.limite_participantes || 0);

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleAddPremio = () => {
    setPremios((prev) => [
      ...prev,
      {
        tipo: "pontos",
        quantidade: 1,
        qtd_pontos: "",
        premio_id: "",
        sortear_ate_participante: "",
      },
    ]);
  };

  const handlePremioChange = (index, field, value) => {
    const atualizados = [...premios];
    atualizados[index][field] = value;
    setPremios(atualizados);
  };

  const handleRemovePremio = (index) => {
    setPremios((prev) => prev.filter((_, i) => i !== index));
  };

  const validarPremios = () => {
    if (premios.length === 0) {
      return "Adicione ao menos um prêmio.";
    }

    for (const p of premios) {
      if (
        !p.quantidade ||
        !p.sortear_ate_participante ||
        (p.tipo === "pontos" && !p.qtd_pontos) ||
        (p.tipo === "fisico" && !p.premio_id)
      ) {
        return "Preencha todos os campos de prêmios corretamente.";
      }
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setMensagem("");
    setErro("");
    setEnviando(true);

    const erroValidacao = validarPremios();

    if (erroValidacao) {
      setErro(erroValidacao);
      setEnviando(false);
      return;
    }

    try {
      await axios.post("/admin/eventos/criar", { ...form, premios });

      setMensagem("Evento criado com sucesso!");
      setEventoCriado(form.slug);

      setForm({
        slug: "",
        nome_evento: "",
        descricao: "",
        data_inicio: "",
        data_fim: "",
        limite_participantes: 150,
        ativo: true,
      });

      setPremios([]);
    } catch (err) {
      console.error(err);
      setErro("Erro ao criar evento.");
    } finally {
      setEnviando(false);
    }
  };

  const handleDownloadQRCode = async () => {
    if (!qrCodeRef.current) return;

    const dataUrl = await htmlToImage.toPng(qrCodeRef.current);
    download(dataUrl, `evento-${eventoCriado}-qrcode.png`);
  };

  return (
    <div className="criarEventoAdmin-page">
      <div className="criarEventoAdmin-topbar">
        <div>
          <span className="criarEventoAdmin-kicker">Eventos promocionais</span>

          <h2>Criar Novo Evento</h2>

          <p>
            Cadastre eventos presenciais com QR Code, defina o limite de
            participantes e configure os prêmios dinâmicos do sorteio.
          </p>
        </div>
      </div>

      {mensagem && (
        <div className="criarEventoAdmin-alert success">
          <CheckCircle2 size={16} />
          {mensagem}
        </div>
      )}

      {erro && <div className="criarEventoAdmin-alert danger">{erro}</div>}

      <form className="criarEventoAdmin-form" onSubmit={handleSubmit}>
        <section className="criarEventoAdmin-card">
          <div className="criarEventoAdmin-cardHeader">
            <div>
              <h3>Dados do evento</h3>
              <span>Informações principais usadas no fluxo público do evento.</span>
            </div>

            <CalendarDays size={18} />
          </div>

          <div className="criarEventoAdmin-grid">
            <div className="criarEventoAdmin-field">
              <label>Slug</label>
              <input
                name="slug"
                value={form.slug}
                onChange={handleFormChange}
                placeholder="ex: evento-tikpag-2026"
                required
              />
            </div>

            <div className="criarEventoAdmin-field">
              <label>Nome do evento</label>
              <input
                name="nome_evento"
                value={form.nome_evento}
                onChange={handleFormChange}
                placeholder="Nome exibido ao participante"
                required
              />
            </div>

            <div className="criarEventoAdmin-field">
              <label>Data início</label>
              <input
                type="date"
                name="data_inicio"
                value={form.data_inicio}
                onChange={handleFormChange}
                required
              />
            </div>

            <div className="criarEventoAdmin-field">
              <label>Data fim</label>
              <input
                type="date"
                name="data_fim"
                value={form.data_fim}
                onChange={handleFormChange}
                required
              />
            </div>

            <div className="criarEventoAdmin-field">
              <label>Participantes máximos</label>
              <input
                type="number"
                name="limite_participantes"
                value={form.limite_participantes}
                onChange={handleFormChange}
              />
            </div>

            <div className="criarEventoAdmin-toggleField">
              <label className="criarEventoAdmin-toggle">
                <input
                  type="checkbox"
                  id="checkbox-ativo"
                  name="ativo"
                  checked={form.ativo}
                  onChange={handleFormChange}
                />

                <span className="criarEventoAdmin-toggleTrack">
                  <span className="criarEventoAdmin-toggleThumb" />
                </span>

                <span>
                  <strong>Evento ativo</strong>
                  <small>Controla se o evento fica disponível ao público.</small>
                </span>
              </label>

              <ToggleLeft size={18} />
            </div>

            <div className="criarEventoAdmin-field criarEventoAdmin-fieldFull">
              <label>Descrição</label>
              <textarea
                name="descricao"
                value={form.descricao}
                onChange={handleFormChange}
                rows={3}
                placeholder="Descrição interna ou pública do evento"
              />
            </div>
          </div>
        </section>

        <section className="criarEventoAdmin-card">
          <div className="criarEventoAdmin-cardHeader">
            <div>
              <h3>Prêmios do evento</h3>
              <span>
                Defina todos os prêmios que serão sorteados e até qual
                participante cada prêmio poderá sair.
              </span>
            </div>

            <Gift size={18} />
          </div>

          <div className="criarEventoAdmin-premiosIntro">
            O total de prêmios deve ser igual ou superior ao número de
            participantes. Prêmios físicos exigem o ID do prêmio cadastrado.
          </div>

          <div className="criarEventoAdmin-premiosLista">
            {premios.map((p, index) => (
              <div key={index} className="criarEventoAdmin-premioItem">
                <div className="criarEventoAdmin-premioTopo">
                  <strong>Prêmio #{index + 1}</strong>

                  <button
                    type="button"
                    className="criarEventoAdmin-removeBtn"
                    onClick={() => handleRemovePremio(index)}
                    title="Remover prêmio"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>

                <div className="criarEventoAdmin-premioGrid">
                  <div className="criarEventoAdmin-field">
                    <label>Tipo de prêmio</label>
                    <select
                      value={p.tipo}
                      onChange={(e) =>
                        handlePremioChange(index, "tipo", e.target.value)
                      }
                    >
                      <option value="pontos">Pontos</option>
                      <option value="fisico">Físico</option>
                    </select>
                  </div>

                  <div className="criarEventoAdmin-field">
                    <label>Quantidade</label>
                    <input
                      type="number"
                      placeholder="Ex: 3 unidades"
                      value={p.quantidade}
                      onChange={(e) =>
                        handlePremioChange(index, "quantidade", e.target.value)
                      }
                    />
                  </div>

                  {p.tipo === "pontos" ? (
                    <div className="criarEventoAdmin-field">
                      <label>Quantidade de pontos</label>
                      <input
                        type="number"
                        placeholder="Ex: 500"
                        value={p.qtd_pontos || ""}
                        onChange={(e) =>
                          handlePremioChange(index, "qtd_pontos", e.target.value)
                        }
                      />
                    </div>
                  ) : (
                    <div className="criarEventoAdmin-field">
                      <label>ID do prêmio físico</label>
                      <input
                        type="number"
                        placeholder="Ex: 164"
                        value={p.premio_id || ""}
                        onChange={(e) =>
                          handlePremioChange(index, "premio_id", e.target.value)
                        }
                      />
                    </div>
                  )}

                  <div className="criarEventoAdmin-field">
                    <label>Disponível até o participante nº</label>
                    <input
                      type="number"
                      placeholder="Ex: 10"
                      value={p.sortear_ate_participante || ""}
                      onChange={(e) =>
                        handlePremioChange(
                          index,
                          "sortear_ate_participante",
                          e.target.value
                        )
                      }
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            className="criarEventoAdmin-addBtn"
            onClick={handleAddPremio}
          >
            <Plus size={16} />
            Adicionar prêmio
          </button>

          <div
            className={`criarEventoAdmin-validacao ${
              premiosInsuficientes ? "danger" : "success"
            }`}
          >
            <div>
              <span>Total de prêmios cadastrados</span>
              <strong>{totalPremios}</strong>
            </div>

            <div>
              <span>Participantes máximos definidos</span>
              <strong>{form.limite_participantes || "—"}</strong>
            </div>

            {premiosInsuficientes && (
              <p>O total de prêmios é inferior ao número de participantes.</p>
            )}
          </div>
        </section>

        <div className="criarEventoAdmin-submitBar">
          <button
            type="submit"
            className="criarEventoAdmin-btn primary"
            disabled={enviando}
          >
            <Save size={16} />
            {enviando ? "Enviando..." : "Criar Evento"}
          </button>
        </div>
      </form>

      {eventoCriado && (
        <section className="criarEventoAdmin-card criarEventoAdmin-resultadoCard">
          <div className="criarEventoAdmin-cardHeader">
            <div>
              <h3>Evento criado</h3>
              <span>Compartilhe o link ou baixe o QR Code para divulgação.</span>
            </div>

            <LinkIcon size={18} />
          </div>

          <div className="criarEventoAdmin-resultado">
            <div className="criarEventoAdmin-linkBox">
              <span>Link do evento</span>
              <code>/eventos/{eventoCriado}</code>
            </div>

            <div ref={qrCodeRef} className="criarEventoAdmin-qrPrint">
              <QRCodeCanvas value={linkEvento} size={148} />
            </div>

            <button
              type="button"
              className="criarEventoAdmin-btn primary"
              onClick={handleDownloadQRCode}
            >
              <Download size={16} />
              Baixar QR Code
            </button>
          </div>
        </section>
      )}
    </div>
  );
};

export default CriarEventoPage;